import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import './App.css';
import { setTokenGetter } from './api';
import Header from './components/Header';
import Footer from './components/Footer';
import LandingPage from './components/LandingPage';
import AuthPage from './components/AuthPage';
import Preview from './components/Preview';
import Workspace from './components/Workspace';
import TrendsHome from './components/TrendsHome';
import CompaniesHome from './components/CompaniesHome';
import EpisodeView from './components/EpisodeView';
import CompanyView from './components/CompanyView';
import SeriesView from './components/SeriesView';
import Legal from './components/Legal';
import ProtectedRoute from './components/ProtectedRoute';

// Paths that render inside the workspace shell rather than the marketing site.
const APP_PATHS = ['/trends', '/companies', '/terms'];

const CLERK_PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

// Dark styling via appearance variables (the @clerk/themes package has ESM
// resolution issues under CRA's webpack, so we don't use its dark baseTheme).
const clerkAppearance = {
  variables: {
    colorPrimary: '#2962ff',
    colorBackground: '#12151c',
    colorText: '#e6e9ef',
    colorTextSecondary: '#8b93a7',
    colorInputBackground: '#0b0e14',
    colorInputText: '#e6e9ef',
    colorNeutral: '#e6e9ef',
    borderRadius: '6px',
    fontFamily: "'Inter', -apple-system, sans-serif",
  },
};

// Clerk's getToken is only reachable from a hook, and the api module needs it on
// every request. Handing it over once here beats threading a token through each
// caller, and it refreshes itself - getToken returns a live token, not a cached
// string, so an expired session renews without the page knowing.
function AuthBridge() {
  const { getToken } = useAuth();
  useEffect(() => setTokenGetter(getToken), [getToken]);
  return null;
}

function AppContent() {
  const location = useLocation();
  // The workspace is a full-viewport surface with its own status bar and footer,
  // so the marketing chrome is left off rather than stacked behind it.
  const isApp = APP_PATHS.some((p) => location.pathname.startsWith(p));

  return (
    <div className={`app ${isApp ? 'app-dashboard' : ''}`}>
      <AuthBridge />
      {!isApp && <Header />}
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/signin/*" element={<AuthPage mode="signin" />} />
        <Route path="/signup/*" element={<AuthPage mode="signup" />} />

        {/* Public on purpose, and it keeps the marketing header so the two
            things a visitor might want next - signing up, going back - are
            where they were a click ago. */}
        <Route path="/preview" element={<Preview />} />

        {/* Under /legal because APP_PATHS already claims the /terms prefix for
            search terms, and a bare /terms-of-service would land in the shell. */}
        <Route path="/legal/privacy" element={<Legal doc="privacy" />} />
        <Route path="/legal/terms" element={<Legal doc="terms" />} />

        {/* A pathless layout route, so the shell stays mounted across
            navigation instead of remounting - and with it the coverage fetch,
            the search overlay and the keyboard listener. */}
        <Route element={<ProtectedRoute><Workspace /></ProtectedRoute>}>
          <Route path="/trends" element={<TrendsHome />} />
          <Route path="/trends/:episodeId" element={<EpisodeView />} />
          <Route path="/companies" element={<CompaniesHome />} />
          <Route path="/companies/:companyId" element={<CompanyView />} />
          <Route path="/terms/:seriesId" element={<SeriesView />} />
        </Route>
      </Routes>
      {!isApp && <Footer />}
    </div>
  );
}

function App() {
  if (!CLERK_PUBLISHABLE_KEY) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: '#0b0e14', color: '#e6e9ef',
        fontFamily: "'Inter', sans-serif", gap: 12, padding: 24, textAlign: 'center',
      }}>
        <h2 style={{ margin: 0 }}>Authentication not configured</h2>
        <p style={{ color: '#8b93a7', maxWidth: 480, lineHeight: 1.5 }}>
          Set <code>REACT_APP_CLERK_PUBLISHABLE_KEY</code> (from the Clerk dashboard, API Keys)
          in <code>frontend/.env.development</code> locally or in the Vercel environment settings,
          then restart the server.
        </p>
      </div>
    );
  }

  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY} appearance={clerkAppearance}>
      <Router>
        <AppContent />
      </Router>
    </ClerkProvider>
  );
}

export default App;
